import { Component, input, signal, computed, ContentChildren, QueryList, AfterContentInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TabItemComponent } from './tab-item.component';
import { ComponentSize } from '../types';

@Component({
  selector: 'app-tabs',
  standalone: true,
  imports: [CommonModule], // TabItemComponent is used via ContentChildren, not in template
  template: `
    <div class="w-full">
      <div [class]="tabListClasses" role="tablist" [attr.aria-orientation]="orientation()">
        @for (tab of tabs(); track tab.id) {
          <button
            type="button"
            role="tab"
            [id]="'tab-' + tab.id"
            [attr.aria-controls]="'panel-' + tab.id"
            [attr.aria-selected]="activeTabId() === tab.id"
            [attr.aria-disabled]="tab.disabled"
            [disabled]="tab.disabled"
            (click)="selectTab(tab.id)"
            [class]="getTabButtonClasses(tab.id, tab.disabled)">
            @if (tab.icon) {
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" [attr.d]="tab.icon" />
              </svg>
            }
            <span>{{ tab.label }}</span>
            @if (tab.badge) {
              <span [class]="getBadgeClasses(tab.badgeColor)">{{ tab.badge }}</span>
            }
          </button>
        }
      </div>
      <div class="mt-4">
        @for (tab of tabs(); track tab.id) {
          <div
            [id]="'panel-' + tab.id"
            role="tabpanel"
            [attr.aria-labelledby]="'tab-' + tab.id"
            [attr.hidden]="activeTabId() !== tab.id"
            [class]="tabPanelClasses">
            <ng-container *ngTemplateOutlet="tab.content"></ng-container>
          </div>
        }
      </div>
    </div>
  `,
  styles: []
})
export class TabsComponent implements AfterContentInit {
  activeTab = input<string>('');
  orientation = input<'horizontal' | 'vertical'>('horizontal');
  variant = input<'default' | 'pills' | 'underline' | 'enclosed'>('default');
  size = input<ComponentSize>('md');

  @ContentChildren(TabItemComponent) tabItems!: QueryList<TabItemComponent>;
  
  activeTabId = signal<string>('');
  tabs = signal<Tab[]>([]);

  ngAfterContentInit(): void {
    const tabList = this.tabItems.map((item, index) => ({
      id: item.id() || `tab-${index}`,
      label: item.label(),
      icon: item.icon(),
      disabled: item.disabled(),
      badge: item.badge(),
      badgeColor: item.badgeColor(),
      content: item.contentTemplate
    }));
    this.tabs.set(tabList);
    
    if (this.activeTab()) {
      this.activeTabId.set(this.activeTab());
    } else if (tabList.length > 0 && !tabList[0].disabled) {
      this.activeTabId.set(tabList[0].id);
    }
  }

  selectTab(tabId: string): void {
    const tab = this.tabs().find(t => t.id === tabId);
    if (tab && !tab.disabled) {
      this.activeTabId.set(tabId);
    }
  }

  getTabButtonClasses(tabId: string, disabled?: boolean): string {
    const classes = [
      'px-4',
      'py-2',
      'font-medium',
      'transition-all',
      'duration-200',
      'focus:outline-none',
      'focus:ring-2',
      'focus:ring-blue-500',
      'focus:ring-offset-2',
      'disabled:opacity-50',
      'disabled:cursor-not-allowed'
    ];

    const sizeMap = {
      xs: 'text-xs px-2 py-1',
      sm: 'text-sm px-3 py-1.5',
      md: 'text-base px-4 py-2',
      lg: 'text-lg px-5 py-2.5',
      xl: 'text-xl px-6 py-3'
    };
    classes.push(sizeMap[this.size()]);

    if (this.variant() === 'pills') {
      classes.push('rounded-full');
      if (this.activeTabId() === tabId) {
        classes.push('bg-blue-600', 'text-white');
      } else {
        classes.push('bg-gray-100', 'dark:bg-gray-700', 'text-gray-700', 'dark:text-gray-300', 'hover:bg-gray-200', 'dark:hover:bg-gray-600');
      }
    } else if (this.variant() === 'underline') {
      classes.push('border-b-2', 'border-transparent');
      if (this.activeTabId() === tabId) {
        classes.push('border-blue-600', 'text-blue-600', 'dark:text-blue-400');
      } else {
        classes.push('text-gray-600', 'dark:text-gray-400', 'hover:border-gray-300', 'hover:text-gray-900', 'dark:hover:text-gray-100');
      }
    } else if (this.variant() === 'enclosed') {
      classes.push('border', 'border-gray-200', 'dark:border-gray-700');
      if (this.activeTabId() === tabId) {
        classes.push('bg-white', 'dark:bg-gray-800', 'border-b-transparent', 'text-blue-600', 'dark:text-blue-400');
      } else {
        classes.push('bg-gray-50', 'dark:bg-gray-700', 'text-gray-600', 'dark:text-gray-400', 'hover:bg-gray-100', 'dark:hover:bg-gray-600');
      }
    } else {
      // default
      if (this.activeTabId() === tabId) {
        classes.push('bg-blue-50', 'dark:bg-blue-900/20', 'text-blue-600', 'dark:text-blue-400', 'border-b-2', 'border-blue-600');
      } else {
        classes.push('text-gray-600', 'dark:text-gray-400', 'hover:bg-gray-50', 'dark:hover:bg-gray-700');
      }
    }

    return classes.join(' ');
  }

  get tabListClasses(): string {
    const classes = ['flex', 'border-b', 'border-gray-200', 'dark:border-gray-700', 'overflow-x-auto'];
    if (this.orientation() === 'vertical') {
      classes.push('flex-col', 'border-b-0', 'border-r');
    }
    return classes.join(' ');
  }

  get tabPanelClasses(): string {
    return 'p-4';
  }

  getBadgeClasses(color?: string): string {
    if (color) return color;
    return 'ml-2 px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
  }
}

interface Tab {
  id: string;
  label: string;
  icon?: string;
  disabled?: boolean;
  badge?: string;
  badgeColor?: string;
  content: any;
}

